import { useState, useMemo } from 'react';
import useAppStore from '../../store/app.store';
import PageHeader from '../../components/ui/PageHeader';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const moeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function FormContrato({ inicial, clientes, onSalvar, onFechar }) {
  const [form, setForm] = useState(inicial || {
    cliente_id: '', numero: '', titulo: '', valor_mensal: '', periodicidade: 'mensal',
    data_inicio: '', data_fim: '', renovacao_automatica: false, condicoes_pagamento: '',
    servicos: [], escopo_midias: { criativos_por_semana: '', plataformas: [] },
    escopo_trafego: { budget_mensal: '', plataformas: [], kpis: {} },
    escopo_comercial: { frequencia_reunioes: '' },
  });

  const toggleServico = (s) => setForm(p => ({
    ...p, servicos: p.servicos.includes(s) ? p.servicos.filter(x => x !== s) : [...p.servicos, s]
  }));

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="form-label">Cliente *</label>
          <select className="form-input" value={form.cliente_id} onChange={e => set('cliente_id', e.target.value)} required>
            <option value="">Selecione o cliente...</option>
            {clientes.filter(c => c.status === 'ativo').map(c => <option key={c.id} value={c.id}>{c.razao_social}</option>)}
          </select>
        </div>
        <div><label className="form-label">Número</label><input className="form-input" placeholder="CT-2025-001" value={form.numero} onChange={e => set('numero', e.target.value)} /></div>
        <div className="col-span-2"><label className="form-label">Título do contrato *</label><input className="form-input" value={form.titulo} onChange={e => set('titulo', e.target.value)} required /></div>
        <div><label className="form-label">Valor mensal (R$)</label><input type="number" className="form-input" value={form.valor_mensal} onChange={e => set('valor_mensal', e.target.value)} /></div>
        <div>
          <label className="form-label">Periodicidade</label>
          <select className="form-input" value={form.periodicidade} onChange={e => set('periodicidade', e.target.value)}>
            <option value="mensal">Mensal</option>
            <option value="trimestral">Trimestral</option>
            <option value="semestral">Semestral</option>
            <option value="anual">Anual</option>
          </select>
        </div>
        <div><label className="form-label">Início *</label><input type="date" className="form-input" value={form.data_inicio} onChange={e => set('data_inicio', e.target.value)} required /></div>
        <div><label className="form-label">Término</label><input type="date" className="form-input" value={form.data_fim} onChange={e => set('data_fim', e.target.value)} /></div>
      </div>

      <div>
        <label className="form-label">Serviços contratados</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {['Mídias Sociais', 'Tráfego Pago', 'Acomp. Comercial', 'Filmagem', 'Design'].map(s => (
            <button key={s} type="button"
              onClick={() => toggleServico(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.servicos.includes(s) ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-300 hover:border-brand-400'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {form.servicos.includes('Mídias Sociais') && (
        <div className="p-4 bg-purple-50 rounded-xl space-y-3">
          <p className="text-xs font-semibold text-purple-700 uppercase">Escopo Mídias</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="form-label">Criativos/semana</label><input type="number" className="form-input" value={form.escopo_midias?.criativos_por_semana} onChange={e => set('escopo_midias', { ...form.escopo_midias, criativos_por_semana: e.target.value })} /></div>
            <div>
              <label className="form-label">Plataformas</label>
              <div className="flex gap-2 flex-wrap mt-1">
                {['instagram', 'facebook', 'tiktok', 'youtube', 'linkedin'].map(p => {
                  const plats = form.escopo_midias?.plataformas || [];
                  return (
                    <button key={p} type="button"
                      onClick={() => set('escopo_midias', { ...form.escopo_midias, plataformas: plats.includes(p) ? plats.filter(x => x !== p) : [...plats, p] })}
                      className={`px-2 py-1 rounded text-xs border transition-colors ${plats.includes(p) ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {form.servicos.includes('Tráfego Pago') && (
        <div className="p-4 bg-blue-50 rounded-xl space-y-3">
          <p className="text-xs font-semibold text-blue-700 uppercase">Escopo Tráfego</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="form-label">Budget mensal (R$)</label><input type="number" className="form-input" value={form.escopo_trafego?.budget_mensal} onChange={e => set('escopo_trafego', { ...form.escopo_trafego, budget_mensal: e.target.value })} /></div>
            <div><label className="form-label">Meta de leads/mês</label><input type="number" className="form-input" value={form.escopo_trafego?.kpis?.leads_meta} onChange={e => set('escopo_trafego', { ...form.escopo_trafego, kpis: { ...form.escopo_trafego?.kpis, leads_meta: e.target.value } })} /></div>
          </div>
        </div>
      )}

      <div><label className="form-label">Condições de pagamento</label><textarea className="form-input" rows={2} value={form.condicoes_pagamento} onChange={e => set('condicoes_pagamento', e.target.value)} /></div>

      <div className="flex gap-2 pt-2">
        <button type="submit" className="btn-primary flex-1">{inicial ? 'Salvar' : 'Criar Contrato'}</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

export default function Contratos({ embedded = false }) {
  const contratos = useAppStore(s => s.contratos);
  const clientes = useAppStore(s => s.clientes);
  const addContrato = useAppStore(s => s.addContrato);
  const updateContrato = useAppStore(s => s.updateContrato);
  const deleteContrato = useAppStore(s => s.deleteContrato);
  const ativarContrato = useAppStore(s => s.ativarContrato);
  const addChamado = useAppStore(s => s.addChamado);
  const addTransacao = useAppStore(s => s.addTransacao);
  const getClienteNome = useAppStore(s => s.getClienteNome);

  const [filtroStatus, setFiltroStatus] = useState('');
  const [modalNovo, setModalNovo] = useState(false);
  const [editando, setEditando] = useState(null);
  const [detalhe, setDetalhe] = useState(null);

  const filtrados = useMemo(() =>
    contratos.filter(c => !filtroStatus || c.status === filtroStatus),
    [contratos, filtroStatus]
  );

  const ativar = (ct) => {
    ativarContrato(ct.id);

    // Gera chamados automáticos para mídias
    if (ct.escopo_midias?.criativos_por_semana) {
      for (let i = 0; i < 4; i++) {
        addChamado({
          titulo: `[AUTO] ${ct.escopo_midias.criativos_por_semana}x criativos — Semana ${i + 1}`,
          descricao: `Demanda gerada automaticamente pelo contrato ${ct.numero || ct.id}.`,
          cliente_id: ct.cliente_id, setor: 'midias', prioridade: 'media',
          data_abertura: new Date().toISOString(),
          prazo_horas: 120,
        });
      }
    }
    // Gera transação financeira
    if (ct.valor_mensal) {
      addTransacao({
        cliente_id: ct.cliente_id, contrato_id: ct.id, tipo: 'receita',
        categoria: 'mensalidade', descricao: `Mensalidade — ${ct.titulo}`,
        valor: ct.valor_mensal,
        data_vencimento: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
      });
    }

    toast.success('Contrato ativado! Chamados e cobrança gerados automaticamente.');
    setDetalhe(null);
  };

  const STATUS_COR = { rascunho: 'cinza', ativo: 'verde', suspenso: 'laranja', encerrado: 'vermelho', cancelado: 'vermelho' };

  return (
    <div>
      {!embedded && (
        <PageHeader
          title="Contratos"
          subtitle={`${contratos.filter(c => c.status === 'ativo').length} contratos ativos`}
          actions={<button onClick={() => setModalNovo(true)} className="btn-primary">+ Novo Contrato</button>}
        />
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button onClick={() => setModalNovo(true)} className="btn-primary">+ Novo Contrato</button>
        </div>
      )}

      <div className="flex gap-3 mb-5">
        <select className="form-input w-auto" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos status</option>
          <option value="rascunho">Rascunho</option>
          <option value="ativo">Ativo</option>
          <option value="suspenso">Suspenso</option>
          <option value="encerrado">Encerrado</option>
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr><th>Número</th><th>Cliente</th><th>Título</th><th>Valor/mês</th><th>Vigência</th><th>Status</th><th>Ações</th></tr>
            </thead>
            <tbody>
              {filtrados.map(ct => (
                <tr key={ct.id}>
                  <td className="font-mono text-xs text-gray-500">{ct.numero || '—'}</td>
                  <td className="font-medium text-gray-800">{getClienteNome(ct.cliente_id)}</td>
                  <td className="text-gray-700 max-w-xs truncate">{ct.titulo}</td>
                  <td className="font-semibold text-brand-700">{ct.valor_mensal ? moeda(ct.valor_mensal) : '—'}</td>
                  <td className="text-xs text-gray-500">
                    {ct.data_inicio ? format(new Date(ct.data_inicio), 'dd/MM/yy') : '—'}
                    {ct.data_fim ? ` até ${format(new Date(ct.data_fim), 'dd/MM/yy')}` : ''}
                  </td>
                  <td><Badge variant={STATUS_COR[ct.status]}>{ct.status}</Badge></td>
                  <td>
                    <div className="flex gap-2 items-center">
                      <button onClick={() => setDetalhe(ct)} className="text-xs text-brand-600 hover:underline font-medium">Ver</button>
                      <button onClick={() => setEditando(ct)} className="text-xs text-gray-500 hover:underline">Editar</button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Excluir o contrato "${ct.titulo}"?`)) {
                            deleteContrato(ct.id);
                            toast.success('Contrato excluído.');
                          }
                        }}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtrados.length && <p className="text-center text-gray-400 py-12 text-sm">Nenhum contrato encontrado.</p>}
        </div>
      </div>

      <Modal open={modalNovo} onClose={() => setModalNovo(false)} title="Novo Contrato" size="xl">
        <FormContrato clientes={clientes} onSalvar={d => { addContrato(d); toast.success('Contrato criado!'); setModalNovo(false); }} onFechar={() => setModalNovo(false)} />
      </Modal>

      <Modal open={!!editando} onClose={() => setEditando(null)} title="Editar Contrato" size="xl">
        {editando && <FormContrato inicial={editando} clientes={clientes} onSalvar={d => { updateContrato(editando.id, d); toast.success('Atualizado!'); setEditando(null); }} onFechar={() => setEditando(null)} />}
      </Modal>

      <Modal open={!!detalhe} onClose={() => setDetalhe(null)} title={detalhe?.titulo} size="lg">
        {detalhe && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Cliente:</span> {getClienteNome(detalhe.cliente_id)}</div>
              <div><span className="text-gray-500">Status:</span> <Badge variant={STATUS_COR[detalhe.status]}>{detalhe.status}</Badge></div>
              <div><span className="text-gray-500">Valor mensal:</span> <strong>{moeda(detalhe.valor_mensal)}</strong></div>
              <div><span className="text-gray-500">Periodicidade:</span> {detalhe.periodicidade}</div>
              <div><span className="text-gray-500">Início:</span> {detalhe.data_inicio ? format(new Date(detalhe.data_inicio), 'dd/MM/yyyy') : '—'}</div>
              <div><span className="text-gray-500">Término:</span> {detalhe.data_fim ? format(new Date(detalhe.data_fim), 'dd/MM/yyyy') : '—'}</div>
            </div>

            {detalhe.servicos?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Serviços</p>
                <div className="flex gap-2 flex-wrap">{detalhe.servicos.map(s => <Badge key={s} variant="azul">{s}</Badge>)}</div>
              </div>
            )}

            {detalhe.escopo_midias && (
              <div className="bg-purple-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-purple-700 mb-1">Escopo Mídias</p>
                <p>{detalhe.escopo_midias.criativos_por_semana}x criativos/semana · {(detalhe.escopo_midias.plataformas || []).join(', ')}</p>
              </div>
            )}
            {detalhe.escopo_trafego && (
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-blue-700 mb-1">Escopo Tráfego</p>
                <p>Budget: {moeda(detalhe.escopo_trafego.budget_mensal)}/mês · Meta: {detalhe.escopo_trafego.kpis?.leads_meta} leads</p>
              </div>
            )}

            {detalhe.status === 'rascunho' && (
              <div className="pt-2 border-t">
                <button onClick={() => ativar(detalhe)}
                  className="btn-primary w-full py-3 text-base">
                  Ativar Contrato (gera chamados + cobrança automaticamente)
                </button>
              </div>
            )}

            {detalhe.ativado_em && (
              <p className="text-xs text-gray-400 text-center">Ativado em {format(new Date(detalhe.ativado_em), 'dd/MM/yyyy HH:mm')}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
